import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "How long does the recovery process take?",
    a: "Recovery can take anywhere from a few days to a couple of weeks. Most swelling and discomfort should subside within a week, but complete healing can take several months.",
  },
  {
    q: "Can I eat normally after my wisdom teeth are removed?",
    a: "You should avoid hard, crunchy, or chewy foods for at least a week. Stick to soft foods like mashed potatoes, applesauce, and soup during the initial recovery period.",
  },
  {
    q: "Will I need stitches after the extraction?",
    a: "In many cases, stitches are required, but they are often dissolvable and don't need to be removed.",
  },
  {
    q: "Is the procedure painful?",
    a: "Most patients don't experience pain during the procedure itself because of local anesthesia. Afterward, there may be some discomfort, but this can be managed with pain medications.",
  },
  {
    q: "Can wisdom teeth grow back?",
    a: "Once a wisdom tooth is extracted, it does not grow back. However, the area where the tooth was removed may heal over time.",
  },
];

const PAGE_PATH = "/wisdom-teeth-removal";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const WisdomTeethRemoval = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Wisdom Tooth Removal in Jaipur | Painless Extraction"
        description="Safe, painless wisdom tooth extraction in Vaishali Nagar, Jaipur. Expert care for impacted wisdom teeth and a smooth recovery. Book with Qualiteeth today."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Wisdom Teeth Removal", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Wisdom Tooth Removal in Jaipur | Painless Extraction",
            description: "Safe, painless wisdom tooth extraction in Vaishali Nagar, Jaipur. Expert care for impacted wisdom teeth and a smooth recovery. Book with Qualiteeth today.",
            procedureName: "Wisdom Tooth Extraction",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Wisdom Tooth Extraction",
            description: "Safe, painless wisdom tooth extraction in Vaishali Nagar, Jaipur. Expert care for impacted wisdom teeth and a smooth recovery. Book with Qualiteeth today.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Wisdom Teeth Removal
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          Wisdom teeth are the third molars that erupt in the late teens or early twenties. When there is not enough space in the jaw they become impacted, causing pain, swelling, infection or crowding of the neighbouring teeth. Removal is done under local anaesthesia and is recommended when the tooth cannot erupt into a healthy, cleanable position.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Are Wisdom Teeth?
            </Heading>
            <Text mt={2} fontWeight="light">
              Wisdom teeth, also known as third molars, are the last set of
              teeth located at the back of your mouth. They are the final teeth
              to emerge, typically appearing between the ages of 17 and 25.
              These teeth are considered vestigial, meaning they were once
              necessary for early humans to chew raw plant materials, but as our
              diets have evolved, they are no longer essential. Many people have
              them removed due to the issues they may cause.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Problems Can a Wisdom Tooth Cause?
            </Heading>
            <Text mt={2} fontWeight="light">
              While some people have no issues with their wisdom teeth, others
              experience a range of problems. Common complications include:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Impaction:</strong> When the wisdom tooth does not
                  fully emerge through the gum line and becomes trapped
                  (impacted) beneath the gum, it can cause pain, swelling, and
                  infection.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Infection:</strong> If the wisdom tooth is partially
                  erupted, bacteria can easily enter the surrounding tissue,
                  causing infections like pericoronitis.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Crowding:</strong> Wisdom teeth can push against
                  neighboring teeth, leading to misalignment or overcrowding,
                  particularly in those who have already had orthodontic work.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Cysts and Tumors:</strong> In rare cases, impacted
                  wisdom teeth can lead to the formation of cysts or tumors,
                  potentially damaging the jawbone and surrounding teeth. color.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Tooth Decay:</strong> Wisdom teeth are located at the
                  back of the mouth, making them hard to clean. This can
                  increase the risk of decay and gum disease.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Symptoms Indicating the Need for Wisdom Teeth Removal
            </Heading>
            <Text mt={2} fontWeight="light">
              If you experience any of the following symptoms, it may be time to
              consult your dentist about the possibility of wisdom tooth
              removal:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Pain or tenderness</strong> in the back of your mouth or
                jaw.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Swelling or infection</strong> around the gums or jaw
                area.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Difficulty opening your mouth</strong> fully due to
                swelling or pain.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bad breath</strong> or an unpleasant taste in your
                mouth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bleeding or gum issues</strong> around the wisdom tooth.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Types of Wisdom Tooth Removal
            </Heading>
            <Text mt={2} fontWeight="light">
              There are two main types of wisdom tooth removal:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Simple Extraction:</strong> This method is used when the
                wisdom tooth has fully erupted and is visible above the gum
                line. The dentist will use forceps to grasp the tooth and remove
                it.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Surgical Extraction:</strong> This is more complex and
                is typically needed when the tooth is impacted or not fully
                erupted. The dentist or oral surgeon will make an incision in
                the gum to access the tooth. In some cases, the tooth may need
                to be broken into smaller pieces to remove it more easily.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Wisdom Tooth Extraction Process
            </Heading>
            <Text mt={2} fontWeight="light">
              The wisdom tooth extraction process involves the following steps:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Consultation and Examination:</strong> The dentist will
                conduct a thorough examination, which may include X-rays, to
                assess the position of your wisdom teeth and determine if they
                need removal.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Anesthesia:</strong> Depending on the complexity of the
                extraction, local anesthesia will be applied to numb the area,
                or you may be put under general anesthesia for more invasive
                extractions.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Removal:</strong> The dentist or oral surgeon will
                extract the tooth. For simple extractions, forceps are used,
                whereas surgical extractions may involve cutting the gum or
                bone.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Closure:</strong> After the tooth is removed, stitches
                may be necessary to close the gum.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Post-operative Care:</strong> You'll be given
                instructions on caring for the extraction site and managing any
                pain or swelling.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Preparing for Wisdom Tooth Extraction
            </Heading>
            <Text mt={2} fontWeight="light">
              Proper preparation for wisdom tooth removal ensures a smoother
              recovery:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Consult your dentist:</strong> Discuss any medications
                you are taking, especially blood thinners, and inform your
                dentist of any allergies.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Arrange transportation:</strong> If you're sedated or
                under general anesthesia, arrange for someone to drive you home
                after the procedure.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Follow pre-op instructions:</strong> Your dentist may
                advise not eating or drinking for several hours before the
                procedure, especially if anesthesia will be used.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Prepare your recovery area:</strong> Have ice packs,
                soft foods, and prescribed medications ready at home.
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Recovery and Aftercare Tips
            </Heading>
            <Text mt={2} fontWeight="light">
              After the procedure, follow these recovery tips to ensure a smooth
              healing process:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Rest:</strong> Take it easy for a few days to allow your
                body to heal.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Pain management:</strong> Use over-the-counter pain
                relievers or those prescribed by your dentist. Ice packs applied
                to your face can also help reduce swelling.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Avoid strenuous activities:</strong> Refrain from heavy
                exercise for at least a week to prevent complications.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Eat soft foods:</strong> Stick to soft foods like mashed
                potatoes, yogurt, or soup for a few days to avoid irritation of
                the extraction site.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Keep the area clean:</strong> Gently rinse your mouth
                with salt water after 24 hours to keep the area clean, but avoid
                vigorous rinsing that could dislodge the clot.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Avoid smoking and drinking through straws:</strong>
                {" These actions can disturb the healing process by dislodging the clot and leading to dry socket."}
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Wisdom Tooth Removal Risks and Benefits
            </Heading>
            <Text mt={2} fontWeight="light">
              Like any surgical procedure, wisdom tooth removal comes with both
              risks and benefits:
            </Text>
            <Text mt={2} fontWeight="bold">
              Benefits:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Prevents future dental issues</strong> such as
                infection, decay, and crowding.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Relieves pain and discomfort</strong> caused by impacted
                or infected wisdom teeth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Improves oral health</strong> by eliminating a potential
                source of bacterial infection.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Reduces the risk of cysts and tumors</strong> that could
                develop around impacted teeth.
              </ListItem>
            </List>

            <Text mt={2} fontWeight="bold">
              Risks:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Infection</strong> is always a risk after any surgery.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Dry socket</strong>, which occurs if the blood clot is
                dislodged too soon, leading to intense pain.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Nerve damage</strong>, though rare, can occur and may
                result in numbness or tingling in the tongue, lip, or chin.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Swelling and bruising</strong> in the face can occur,
                especially after surgical extractions.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default WisdomTeethRemoval;
